/**
 * Pure date-grouping helpers for the Ask Puntt chat timeline.
 *
 * The chat renders a TIME on each message row but no DATE, so messages from
 * previous days are ambiguous. To add Slack-style date context we insert a
 * centered day-divider above the first message of each calendar day. These
 * helpers contain the (testable) day-boundary + label logic so the giant
 * `chat.tsx` render body stays out of the date math.
 *
 * Determinism: `formatDayDivider` takes an explicit `now` so "Today" /
 * "Yesterday" are computed relative to a caller-supplied reference instead of
 * the real wall clock. This keeps the unit tests deterministic.
 */
import { format, isValid } from "date-fns";

/** Coerce a Date | string | number into a Date, or null when unparseable. */
function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isValid(d) ? d : null;
}

/**
 * True when `a` and `b` fall on the same calendar day in local time. Invalid /
 * missing inputs are never "the same day" (a divider should still render rather
 * than silently grouping an undated message with its neighbour).
 */
export function isSameCalendarDay(
  a: Date | string | number | null | undefined,
  b: Date | string | number | null | undefined,
): boolean {
  const da = toDate(a);
  const db = toDate(b);
  if (!da || !db) return false;
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

/** Whole-day difference (local calendar) between `now` and `date`. */
function calendarDayDelta(date: Date, now: Date): number {
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
  const startOfNow = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((startOfNow - startOfDate) / MS_PER_DAY);
}

/**
 * Friendly divider label for `date` relative to `now`:
 *  - same calendar day        -> "Today"
 *  - the calendar day before  -> "Yesterday"
 *  - same calendar year       -> "Monday, June 9"
 *  - a different year         -> "June 9, 2026"
 *
 * Returns "" for an unparseable date so the caller can skip the divider.
 */
export function formatDayDivider(
  date: Date | string | number | null | undefined,
  now: Date | string | number = new Date(),
): string {
  const d = toDate(date);
  const reference = toDate(now);
  if (!d || !reference) return "";

  if (isSameCalendarDay(d, reference)) return "Today";

  const delta = calendarDayDelta(d, reference);
  if (delta === 1) return "Yesterday";

  if (d.getFullYear() === reference.getFullYear()) {
    return format(d, "EEEE, MMMM d");
  }
  return format(d, "MMMM d, yyyy");
}

/**
 * Stable local-calendar-day key for a timestamp, e.g. "2026-06-09". Used as a
 * React key + `data-day` attribute so each day-divider is identifiable and
 * de-duplicated. Returns "" for an unparseable input.
 */
export function calendarDayKey(
  value: Date | string | number | null | undefined,
): string {
  const d = toDate(value);
  if (!d) return "";
  return format(d, "yyyy-MM-dd");
}

/**
 * Full localized "date at time" string for a message timestamp tooltip, e.g.
 * "June 9, 2026 at 7:36 PM". Returns "" for an unparseable input.
 */
export function formatFullDateTime(
  value: Date | string | number | null | undefined,
): string {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
